import { useCallback, useEffect, useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { DASHBOARD_KEY } from '../../constants/kpi.constants';
import { KpiDashboardMetaResponseDto, MetricDetailsResponseDto } from '../../types/response';
import KpiDetailCardComponent from './kpi-detail-card';

interface KpiDetailCardProps {
  meta: KpiDashboardMetaResponseDto;
  className?: string;
}

function buildDetailQuery(fromWeek: number, fromYear: number, toWeek: number, toYear: number): string {
  const params = new URLSearchParams();
  params.set('from[week]', String(fromWeek));
  params.set('from[year]', String(fromYear));
  params.set('to[week]', String(toWeek));
  params.set('to[year]', String(toYear));
  return `?${params.toString()}`;
}

function resolveInitialFromWeek(meta: KpiDashboardMetaResponseDto): { fromYear: number; fromWeek: number } {
  const fromYear = meta.currentYear - 1;
  const prevYearWeeks = meta.years.find((y) => y.year === fromYear)?.weeks ?? [];

  if (prevYearWeeks.includes(meta.currentWeek)) {
    return { fromYear, fromWeek: meta.currentWeek };
  }

  // Nimm die letzte existente KW des Vorjahres
  const fallbackWeek = prevYearWeeks[prevYearWeeks.length - 1] ?? meta.currentWeek;
  return { fromYear, fromWeek: fallbackWeek };
}

const KpiDetailCard = ({ meta, className }: KpiDetailCardProps) => {
  const { apiRequest } = useApiRequest();

  // Selector State – initial erste Section/Card/Metric
  const firstSection = meta.sections[0];
  const firstCard = firstSection?.cards[0];
  const firstMetric = firstCard?.values[0];

  const [selectedSectionKey, setSelectedSectionKey] = useState(firstSection?.sectionKey ?? '');
  const [selectedCardKey, setSelectedCardKey] = useState(firstCard?.cardKey ?? '');
  const [selectedMetricKey, setSelectedMetricKey] = useState(firstMetric?.metricKey ?? '');

  // Range State
  const { fromYear: initialFromYear, fromWeek: initialFromWeek } = resolveInitialFromWeek(meta);
  const [fromYear, setFromYear] = useState(initialFromYear);
  const [fromWeek, setFromWeek] = useState(initialFromWeek);
  const [toYear, setToYear] = useState(meta.currentYear);
  const [toWeek, setToWeek] = useState(meta.currentWeek);

  const [details, setDetails] = useState<MetricDetailsResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const canFetch = !!selectedSectionKey && !!selectedCardKey && !!selectedMetricKey;

  const fetchDetails = useCallback(
    async (sectionKey: string, cardKey: string, metricKey: string, fWeek: number, fYear: number, tWeek: number, tYear: number) => {
      if (!sectionKey || !cardKey || !metricKey) return;
      setIsLoading(true);
      try {
        const query = buildDetailQuery(fWeek, fYear, tWeek, tYear);
        const response = await apiRequest<MetricDetailsResponseDto>(
          `kpi-dashboards/${DASHBOARD_KEY}/${sectionKey}/${cardKey}/${metricKey}/details${query}`,
          'GET'
        );
        if (response.data) setDetails(response.data);
      } catch (error) {
        console.error('Error fetching metric details:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [apiRequest]
  );

  useEffect(() => {
    if (!canFetch) return;
    fetchDetails(selectedSectionKey, selectedCardKey, selectedMetricKey, fromWeek, fromYear, toWeek, toYear);
  }, [selectedSectionKey, selectedCardKey, selectedMetricKey, fromWeek, fromYear, toWeek, toYear, canFetch, fetchDetails]);

  const handleSectionChange = (sectionKey: string) => {
    const section = meta.sections.find((s) => s.sectionKey === sectionKey);
    const firstCard = section?.cards[0];
    const firstMetric = firstCard?.values[0];
    setSelectedSectionKey(sectionKey);
    setSelectedCardKey(firstCard?.cardKey ?? '');
    setSelectedMetricKey(firstMetric?.metricKey ?? '');
  };

  const handleCardChange = (cardKey: string) => {
    const section = meta.sections.find((s) => s.sectionKey === selectedSectionKey);
    const card = section?.cards.find((c) => c.cardKey === cardKey);
    const firstMetric = card?.values[0];
    setSelectedCardKey(cardKey);
    setSelectedMetricKey(firstMetric?.metricKey ?? '');
  };

  const handleRangeChange = (range: { from: { week: string; year: string }; to: { week: string; year: string } }) => {
    setFromWeek(Number(range.from.week));
    setFromYear(Number(range.from.year));
    setToWeek(Number(range.to.week));
    setToYear(Number(range.to.year));
  };

  return (
    <KpiDetailCardComponent
      className={className}
      years={meta.years}
      sections={meta.sections}
      selectedSectionKey={selectedSectionKey}
      selectedCardKey={selectedCardKey}
      selectedMetricKey={selectedMetricKey}
      range={{ fromYear, fromWeek, toYear, toWeek }}
      details={details}
      isLoading={isLoading}
      onSectionChange={handleSectionChange}
      onCardChange={handleCardChange}
      onMetricChange={setSelectedMetricKey}
      onRangeChange={handleRangeChange}
    />
  );
};

export default KpiDetailCard;
